import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import { format } from "date-fns";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import API_BASE_URL from "../../../../Constants";
import { HeadersContext } from "./headersContext";
import NavHeightAdjust from "../../../../components/navbar/NavHeightAdjust";
import { selectNavHeight } from "../../../../redux/navbar/navHeightSlice";
import DefectCreationStep from "./ELV3Forms/components/Elv3Form/DefectCreationStep";
import { useSelector } from "react-redux";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return format(date, "MM/dd/yyyy");
};

const DeviceDataDefects = () => {
  const { deviceID } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [deviceDefects, setDeviceDefects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { headers: contextHeaders } = useContext(HeadersContext);
  const headers = location.state?.headers || contextHeaders;

  const navHeight = useSelector(selectNavHeight);

  // state for editing defects
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDefectDialogOpen, setIsDefectDialogOpen] = useState(false);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [isTestMode, setIsTestMode] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!deviceID) {
        console.error("No deviceID available");
        return;
      }
      try {
        const allDevicesResponse = await fetch(
          `${API_BASE_URL}/api/admin/getDobDevicesPage?page=0&size=10000`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (!allDevicesResponse.ok) {
          throw new Error(`HTTP error! status: ${allDevicesResponse.status}`);
        }

        const allDevicesData = await allDevicesResponse.json();
        const foundDevice = allDevicesData.content.find(
          (device) => device.deviceID === deviceID
        );

        const defectsResponse = await fetch(
          `${API_BASE_URL}/api/admin/elv3defects/device/${deviceID}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (!defectsResponse.ok) {
          throw new Error(`HTTP error! status: ${defectsResponse.status}`);
        }

        const defectsData = await defectsResponse.json();

        setDeviceInfo(foundDevice);
        setDeviceDefects(
          Array.isArray(defectsData) ? defectsData : [defectsData]
        );
      } catch (error) {
        console.error("Fetch error:", error.message || error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [deviceID]);

  const deviceInfoFields = [
    { label: "Device ID", value: deviceInfo && deviceInfo.deviceID },
    { label: "Device Type", value: deviceInfo && deviceInfo.deviceType },
    { label: "Premises", value: deviceInfo && deviceInfo.location },
    { label: "Borough", value: deviceInfo && deviceInfo.borough },
    { label: "Block", value: deviceInfo && deviceInfo.block },
    { label: "Lot", value: deviceInfo && deviceInfo.lot },
    { label: "BIN", value: deviceInfo && deviceInfo.bin },
    { label: "Approved Date", value: deviceInfo && deviceInfo.approvedDate },
    { label: "Status Date", value: deviceInfo && deviceInfo.statusDate },
    { label: "Device Domain", value: deviceInfo && deviceInfo.deviceDomain },
  ];

  const handleEditDefect = (defect) => {
    setSelectedDefect(defect);

    setIsDefectDialogOpen(true);
  };

  const handleAddDefect = () => {
    setSelectedDefect(null);

    setIsDefectDialogOpen(true);
  };

  const handleDeleteDefect = async (defectId) => {
    if (window.confirm("Are you sure you want to delete this defect?")) {
      if (!isTestMode) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/admin/elv3defects/${defectId}`,

            {
              method: "DELETE",

              credentials: "include",
            }
          );

          if (!response.ok) {
            throw new Error("Failed to delete defect");
          }

          setDeviceDefects((prevDefects) =>
            prevDefects.filter((d) => d.id !== defectId)
          );
        } catch (error) {
          console.error("Error deleting defect:", error);

          alert("Failed to delete defect");
        }
      } else {
        // Test mode deletion

        setDeviceDefects((prevDefects) =>
          prevDefects.filter((d) => d.id !== defectId)
        );
      }
    }
  };

  const handleDefectSubmit = async (defectData) => {
    console.log("Defect Data:", defectData);
    // try {
    //   const response = await fetch(
    //     `${API_BASE_URL}/api/inspections/posting/CreateDefect`,
    //     {
    //       method: "POST",
    //       headers: {
    //         "Content-Type": "application/json",
    //         authtoken: headers?.authtoken || "",
    //         systemuser: headers?.systemuser || "",
    //       },
    //       body: JSON.stringify(defectData),
    //     }
    //   );
    //   if (!response.ok) {
    //     throw new Error("Failed to create defect");
    //   }
    //   const newDefect = await response.json();
    //   setDeviceDefects((prev) => [...prev, newDefect]);
    // } catch (error) {
    //   console.error("Error creating defect:", error);
    //   alert("Failed to create defect");
    // }
    setIsDefectDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <CircularProgress />
      </div>
    );
  }

  return (
    <NavHeightAdjust>
      <Box className="p-6">
        {/* Top Gradient Header */}
        <Box className="flex gradient-bg text-white p-4 rounded-md shadow-md mb-4 border border-gray-900 relative justify-between">
          {/* Back Button */}
          <div className="flex">
            <button
              onClick={() => navigate(-1)}
              className="bg-transparent rounded-full shadow-none hover:bg-black/10 focus:outline-none mr-8 p-2"
            >
              <ArrowBackIcon />
            </button>
            <Typography variant="h4" fontWeight="bold">
              {deviceID}
            </Typography>
          </div>
          <button onClick={handleAddDefect}>
            <AddIcon />
            Add/Modify Defect
          </button>
        </Box>
        {/* Device Info and Defects Section */}
        <Box sx={{ display: "flex", gap: "24px" }}>
          {/* Left Column - Device Information */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#f5f5f5",
              padding: "16px",
              borderRadius: "8px",
              border: "2px solid #ddd",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Device Info
            </Typography>
            <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              {deviceInfoFields.map((field, index) => (
                <Box key={index} className="w-full">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    {field.label}
                  </label>
                  <input
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={field.value || "N/A"}
                    readOnly
                    disabled
                  />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right Column - Device Defects */}
          <Box
            sx={{
              flex: 2,
              backgroundColor: "#fff",
              padding: "16px",
              borderRadius: "8px",
              border: "2px solid #ddd",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Device Defects
            </Typography>
            <Box className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Box>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Periodic Inspection Date
                </label>
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={
                    deviceInfo && deviceInfo.inspectionDate
                      ? formatDate(deviceInfo.inspectionDate)
                      : "N/A"
                  }
                  readOnly
                  disabled
                />
              </Box>
              <Box>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Defects Exist?
                </label>
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={deviceDefects.length > 0 ? "Yes" : "No"}
                  readOnly
                  disabled
                />
              </Box>
            </Box>

            <Typography variant="h5" component="h3" gutterBottom>
              Manage Defects
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Elevator Part</TableCell>
                    <TableCell>Elevator Sub-Part</TableCell>
                    <TableCell>Violating Condition</TableCell>
                    <TableCell>Suggested Remedy</TableCell>
                    <TableCell>Comments</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deviceDefects.length > 0 ? (
                    deviceDefects.map((defect, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {defect.elevatorPartLabel || "N/A"}
                        </TableCell>
                        <TableCell>
                          {defect.elevatorSubPartLabel || "N/A"}
                        </TableCell>
                        <TableCell>
                          {defect.violationConditionLabel || "N/A"}
                        </TableCell>
                        <TableCell>
                          {defect.suggestedRemedyLabel || "N/A"}
                        </TableCell>
                        <TableCell>{defect.comments || "N/A"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        No defects found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography className="mt-4 text-sm text-gray-600">
              Total Items: {deviceDefects.length}
            </Typography>
          </Box>
        </Box>
        <Dialog
          open={isDefectDialogOpen}
          onClose={() => setIsDefectDialogOpen(false)}
          maxWidth={false}
          fullWidth
          PaperProps={{
            style: {
              position: "absolute",
              top: navHeight,
              left: "50%",
              transform: "translateX(-50%)",
              margin: 4,
              transition: "width 0.3s ease, height 0.3s ease",
              width: "1000px",
              height: `calc(100vh - ${navHeight + 8}px)`,
              overflow: "auto",
            },
          }}
          style={{ zIndex: 1400 }}
        >
          <DefectCreationStep
            selectedDevices={[
              {
                DeviceID: deviceInfo?.deviceID || "TEST-001",
                DeviceTypeLabel: deviceInfo?.deviceType || "Test Device",
                InspectionType: "Category 1",
              },
            ]}
            elv3Guid={isTestMode ? "test-guid" : null}
            onSubmit={handleDefectSubmit}
            testMode={isTestMode}
            initialDefect={selectedDefect}
          />
        </Dialog>
      </Box>
    </NavHeightAdjust>
  );
};

export default DeviceDataDefects;
